import type { CampDiscovery, CampView } from '../../shared/camp'
import type { FoodView } from '../../shared/food'
import type { ClientMsg } from '../../shared/protocol'
import { TOWN_HALL_PHASES, townHallBonuses, townHallPhase } from '../../shared/townhall'
import { councilArt, resourceIcon } from './councilArt'
import { el } from './panels'

type Action = (label: string, key: string, message: ClientMsg, disabled?: boolean) => HTMLButtonElement

const DISCOVERY: Record<CampDiscovery['kind'], { resource: 'wood' | 'food' | 'stone' | 'iron'; use: string }> = {
  timber: { resource: 'wood', use: 'Gathering → Chop Trees supplies wood from this grove.' },
  fertile: { resource: 'food', use: 'Place a farm here for a stronger harvest.' },
  stone: { resource: 'stone', use: 'A quarry here supplies building stone.' },
  iron: { resource: 'iron', use: 'A nearby mine supplies iron for metalworking.' },
}

const PHASE_ART = ['camp', 'settlement', 'timberhall', 'fortifiedhall', 'keep'] as const

const count = (value: number): string => String(Math.max(0, Math.floor(value)))
const rate = (value: number): string => Math.abs(value).toFixed(1)

export function wildlifeTime(until: number | undefined, tick: number): string | null {
  if (until === undefined || until <= tick) return null
  const seconds = Math.ceil(until - tick)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** Update the existing Council entry point; the project has no floating actions. */
export function renderCampLauncher(launch: HTMLButtonElement, camp: CampView, tick: number): void {
  const protectedFor = wildlifeTime(camp.wildlifeUntil, tick)
  const heading = camp.completed
    ? camp.watchEnabled ? `Village watch · ${camp.watchCount} / 2` : 'Settler Camp established'
    : camp.active ? `Settler Camp · ${count(camp.wood)} / ${count(camp.woodTarget)} wood` : 'Build a Settler Camp'
  const hint = camp.completed ? camp.status
    : camp.active ? camp.status : 'Choose a project for your village'
  const eyebrow = el('span', 'journal-eyebrow', protectedFor ? `Village Council · Wildlife safe ${protectedFor}` : 'Village Council · J')
  const status = el('span', 'journal-launch-hint camp-launch-status', hint)
  status.title = hint
  launch.replaceChildren(eyebrow, el('strong', '', heading), status)
  if (camp.active && !camp.completed) {
    const track = el('span', 'camp-launch-progress')
    track.setAttribute('aria-hidden', 'true')
    const fill = el('span')
    fill.style.width = `${Math.min(100, Math.max(0, camp.wood / Math.max(1, camp.woodTarget) * 100))}%`
    track.append(fill); launch.append(track)
  }
  launch.classList.add('camp-launch')
  launch.classList.toggle('ready', camp.ready && !camp.completed)
  launch.setAttribute('aria-label', `Open Village Council (J). ${heading}. ${hint}`)
}

/** Shared discovery rows use only permanent navigation, never place buildings. */
export function discoveryList(discoveries: CampDiscovery[], show: (find: CampDiscovery) => void, focusAttribute: 'action' | 'exploreAction'): HTMLElement {
  const list = el('div', 'camp-discoveries')
  for (const find of [...discoveries].sort((a, b) => b.tick - a.tick)) {
    const def = DISCOVERY[find.kind]
    const row = el('article', 'camp-discovery')
    const portrait = el('span', `camp-discovery-portrait ${find.kind}`)
    portrait.append(resourceIcon(def.resource))
    const words = el('div', 'camp-discovery-copy')
    words.append(el('strong', '', find.name),
      el('span', 'camp-discovery-by', `Found by ${find.by} · ${find.q}, ${find.r}`),
      el('p', '', def.use))
    const button = el('button', 'btn btn-sm', 'Show on map')
    button.dataset[focusAttribute] = `discovery-${find.id}`
    button.setAttribute('aria-label', `Show ${find.name} on the map`)
    button.onclick = () => show(find)
    row.append(portrait, words, button); list.append(row)
  }
  return list
}

export function campProjectCard(camp: CampView, tick: number, action: Action, show: (find: CampDiscovery) => void): HTMLElement {
  const card = el('article', 'journal-card camp-project-card')
  card.dataset.campProject = ''
  const phase = townHallPhase(camp.phase)
  card.append(el('span', 'camp-project-eyebrow', `Settlement · Stage ${phase} of 5`),
    el('h3', '', camp.completed ? TOWN_HALL_PHASES[phase - 1].name : 'Grow your campsite'))
  if (!camp.completed) {
    const evolution = el('div', 'camp-evolution')
    for (const [index, caption] of [[0, 'Your home'], [1, 'Next upgrade']] as const) {
      if (index === 1) evolution.append(el('span', 'camp-stage-arrow', '→'))
      const stage = el('div', `camp-stage${index === 1 ? ' next' : ''}`)
      stage.append(councilArt(PHASE_ART[index], 'camp-stage-art'),
        el('span', 'camp-stage-caption', caption), el('strong', '', TOWN_HALL_PHASES[index].name))
      evolution.append(stage)
    }
    const bonuses = townHallBonuses(2)
    const benefits = el('div', 'camp-benefits')
    for (const [kind, text] of [['house', `+${bonuses.housing} housing`], ['storage', `+${bonuses.storage} storage`], ['watch', 'Village watch']] as const) {
      const benefit = el('span', 'camp-benefit')
      benefit.append(councilArt(kind), el('span', '', text)); benefits.append(benefit)
    }
    card.append(evolution, benefits)
    const line = el('div', 'camp-project-total')
    const resource = el('span', 'camp-resource-label')
    resource.append(resourceIcon('wood'), el('span', '', 'Wood saved'))
    line.append(resource, el('strong', '', `${count(camp.wood)} / ${count(camp.woodTarget)}`))
    const progress = el('progress', 'camp-project-meter')
    progress.max = Math.max(1, camp.woodTarget)
    progress.value = Math.max(0, Math.min(camp.wood, camp.woodTarget))
    progress.setAttribute('aria-label', 'Wood saved for the Settler Camp')
    progress.setAttribute('aria-valuetext', `${count(camp.wood)} of ${count(camp.woodTarget)} wood`)
    card.append(line, progress)
    const status = el('p', 'camp-project-status', camp.ready
      ? 'Ready to upgrade. Select your Campsite → Info → Upgrade.'
      : camp.storageNeeded > 0 && camp.active ? 'Add wood storage to finish this project.'
      : camp.active ? camp.status : 'Choose this goal to focus spare workers on gathering wood.')
    status.title = camp.status
    status.setAttribute('role', 'status')
    card.append(status)
    const storage = el('div', camp.storageNeeded > 0 ? 'camp-storage-needed camp-storage-note' : 'camp-storage-room camp-storage-note')
    storage.append(councilArt('storage'), el('span', '', camp.storageNeeded > 0
      ? `Need room for ${Math.ceil(camp.storageNeeded)} more wood`
      : `Room for ${count(camp.storageFree)} more wood`))
    storage.title = 'Build a Storehouse from Town, or change wood limits in a building’s Storage tab. Food and materials share capacity.'
    card.append(storage, action(camp.active ? 'Pause camp project' : 'Choose camp project', 'camp-project', { t: 'campProject', active: !camp.active }))
  } else {
    const established = el('div', 'camp-established')
    established.append(councilArt(PHASE_ART[phase - 1], 'camp-established-art'))
    const watchGroup = el('div', 'camp-watch-group')
    watchGroup.append(el('span', 'camp-stage-caption', 'Village watch'),
      el('strong', '', `${camp.watchCount} / 2 volunteers`))
    const slots = el('div', 'camp-watch-slots')
    for (let i = 0; i < 2; i++) {
      const occupied = i < camp.watchCount
      const slot = el('div', `camp-watch-slot${occupied ? ' occupied' : ''}`)
      slot.append(councilArt('watch'), el('span', '', occupied ? 'On watch' : 'Open spot'))
      slots.append(slot)
    }
    watchGroup.append(slots); established.append(watchGroup); card.append(established)
    const watch = el('div', 'camp-watch-status')
    watch.append(el('strong', '', camp.watchEnabled ? 'Watch enabled' : 'Watch is off'),
      el('span', '', camp.status))
    card.append(watch, el('p', 'camp-watch-explainer', 'Spare villagers equip crafted spears and patrol your home.'),
      action(camp.watchEnabled ? 'Stand down the watch' : 'Form a village watch', 'village-watch', { t: 'villageWatch', enabled: !camp.watchEnabled }))
  }
  const protectedFor = wildlifeTime(camp.wildlifeUntil, tick)
  if (camp.wildlifeUntil !== undefined) {
    const protection = el('div', 'camp-wildlife-time')
    const timer = el('strong', '', protectedFor ? `${protectedFor} safe` : 'Protection ended')
    timer.dataset.wildlifeUntil = String(camp.wildlifeUntil)
    protection.append(councilArt('wildlife'), el('span', '', 'Wildlife'), timer)
    protection.title = 'Animals leave your villagers alone for the first two game days. Afterward, keep them near shelter or defenders.'
    card.append(protection)
  }
  if (camp.discoveries.length) {
    const places = el('details', 'camp-found-places')
    places.append(el('summary', '', `${camp.discoveries.length} ${camp.discoveries.length === 1 ? 'place' : 'places'} found · View map pins`), discoveryList(camp.discoveries, show, 'action'))
    card.append(places)
  } else card.append(el('p', 'camp-discovery-hint', 'Wandering villagers mark useful places on your map.'))
  return card
}

export function villageFoodCard(food: FoodView, population: number, action: Action, storedFood = 0, popCap = population): HTMLElement {
  const card = el('article', 'journal-card camp-food-card')
  const heading = el('div', 'camp-food-heading')
  const title = el('div')
  title.append(el('span', 'camp-project-eyebrow', 'Keep your village growing'), el('h3', '', 'Food & new villagers'))
  heading.append(title, councilArt('farm', 'camp-food-art')); card.append(heading)
  const metrics = el('div', 'camp-food-metrics')
  for (const [label, value, unit, kind] of [
    ['Stored food', storedFood > 0 && storedFood < 1 ? '< 1' : count(storedFood), 'available', 'stored'],
    ['Making', rate(food.production), '/ min', 'making'],
    ['Eating', rate(food.consumption), '/ min', 'eating'],
  ] as const) {
    const metric = el('div', `camp-food-metric ${kind}`)
    metric.append(el('span', 'camp-metric-label', label), el('strong', '', value), el('span', 'camp-metric-unit', unit)); metrics.append(metric)
  }
  card.append(metrics)
  const balance = el('p', 'camp-food-balance' + (food.net < -0.05 ? ' short' : ''), food.net < -0.05
    ? `↓ ${rate(food.net)} food / min · Using stored supplies`
    : `↑ ${rate(Math.max(0, food.net))} food / min · Current surplus`)
  balance.title = 'Current production and consumption help you plan food. A food deficit does not stop arrivals while food remains in storage.'
  card.append(balance)
  const arrivalTitle = el('div', 'camp-arrival-title')
  arrivalTitle.append(el('h4', '', 'Ready for another villager?'), el('span', 'camp-arrival-cadence', 'Checks every 45s'))
  card.append(arrivalTitle)
  const checks = el('div', 'camp-arrival-checks')
  for (const [kind, ok, label, detail] of [
    ['farm', storedFood > 0, 'Food above zero', storedFood > 0 ? 'Supplies available' : 'Food storage is empty'],
    ['house', population < popCap, 'A free home', `${population} / ${popCap} housing used`],
  ] as const) {
    const check = el('div', `camp-arrival-check${ok ? ' met' : ' missing'}`)
    const text = el('div'); text.append(el('strong', '', label), el('span', '', detail))
    const indicator = el('span', 'camp-check-mark', ok ? '✓' : '!')
    indicator.setAttribute('aria-label', ok ? 'Ready' : 'Needed')
    check.append(councilArt(kind), text, indicator); checks.append(check)
  }
  card.append(checks)
  const controls = el('div', 'camp-growth-choices')
  controls.setAttribute('role', 'group'); controls.setAttribute('aria-label', 'New villager arrivals')
  for (const [mode, label] of [['steady', 'Welcome new arrivals'], ['paused', 'Pause arrivals']] as const) {
    const selected = food.growth === mode
    const button = action(label, `growth-${mode}`, { t: 'growth', mode }, selected)
    button.setAttribute('aria-pressed', String(selected)); controls.append(button)
  }
  const readiness = el('p', `camp-growth-reason${food.canGrow ? ' ready' : ''}`, food.growth === 'paused'
    ? 'Arrivals paused. Your villagers keep working.'
    : food.canGrow ? 'Ready. Your next villager can arrive at the next check.' : food.reason)
  readiness.setAttribute('role', 'status')
  card.append(controls, readiness)
  return card
}
